import {
  Children,
  cloneElement,
  isValidElement,
  useRef,
  type ReactElement,
  type ReactNode
} from "react";
import { CSSTransition, SwitchTransition, TransitionGroup } from "react-transition-group";
import {
  assignRef,
  extractRef,
  mergeClasses,
  resolveTimeout,
  type TransitionDuration
} from "./vueTransitionHelpers";

type TransitionMode = "out-in" | "in-out";
type TransitionHook = (el: Element) => void;

export type VueTransitionProps = {
  children?: ReactNode;
  name?: string;
  mode?: TransitionMode;
  appear?: boolean;
  duration?: TransitionDuration;
  enterFromClass?: string;
  enterActiveClass?: string;
  enterToClass?: string;
  leaveFromClass?: string;
  leaveActiveClass?: string;
  leaveToClass?: string;
  onBeforeEnter?: TransitionHook;
  onEnter?: TransitionHook;
  onAfterEnter?: TransitionHook;
  onBeforeLeave?: TransitionHook;
  onLeave?: TransitionHook;
  onAfterLeave?: TransitionHook;
};

function resolveChild(children: ReactNode): ReactElement | null {
  const first = Children.toArray(children).find(
    // @ts-expect-error
    child => child !== null && child !== undefined && child !== false
  );
  if (!first || !isValidElement(first)) return null;
  return first;
}

export function VueTransition({
  children,
  name = "v",
  mode,
  appear = false,
  duration,
  enterFromClass,
  enterActiveClass,
  enterToClass,
  leaveFromClass,
  leaveActiveClass,
  leaveToClass,
  onBeforeEnter,
  onEnter,
  onAfterEnter,
  onBeforeLeave,
  onLeave,
  onAfterLeave
}: VueTransitionProps): ReactElement | null {
  const child = resolveChild(children);
  if (!child) return null;

  // react-transition-group expects a real DOM child for class toggling.
  if (typeof child.type !== "string") return child;
  const nodeRefs = useRef(new Map<string, { current: Element | null }>());
  const childKey = String(child.key ?? "__vue-transition__");
  const nodeRef = (() => {
    const existing = nodeRefs.current.get(childKey);
    if (existing) return existing;
    const created = { current: null as Element | null };
    nodeRefs.current.set(childKey, created);
    return created;
  })();
  const existingRef = extractRef(child);
  const childWithRef = cloneElement(child, {
    // @ts-expect-error
    ref: (node: Element | null) => {
      nodeRef.current = node;
      assignRef(existingRef, node);
    }
  });

  const resolvedEnterFrom = enterFromClass ?? `${name}-enter-from`;
  const resolvedEnterActive = enterActiveClass ?? `${name}-enter-active`;
  const resolvedEnterTo = enterToClass ?? `${name}-enter-to`;
  const resolvedLeaveFrom = leaveFromClass ?? `${name}-leave-from`;
  const resolvedLeaveActive = leaveActiveClass ?? `${name}-leave-active`;
  const resolvedLeaveTo = leaveToClass ?? `${name}-leave-to`;

  const classNames = {
    appear: resolvedEnterFrom,
    appearActive: mergeClasses(resolvedEnterActive, resolvedEnterTo),
    appearDone: "",
    enter: resolvedEnterFrom,
    enterActive: mergeClasses(resolvedEnterActive, resolvedEnterTo),
    enterDone: "",
    exit: resolvedLeaveFrom,
    exitActive: mergeClasses(resolvedLeaveActive, resolvedLeaveTo),
    exitDone: ""
  };

  const transitionNode = (
    <CSSTransition
      key={childKey}
      timeout={resolveTimeout(duration)}
      classNames={classNames}
      appear={appear}
      // @ts-expect-error
      nodeRef={nodeRef}
      onEnter={() => {
        if (!nodeRef.current) return;
        onBeforeEnter?.(nodeRef.current);
        onEnter?.(nodeRef.current);
      }}
      onEntered={() => {
        if (!nodeRef.current) return;
        onAfterEnter?.(nodeRef.current);
      }}
      onExit={() => {
        if (!nodeRef.current) return;
        onBeforeLeave?.(nodeRef.current);
      }}
      onExiting={() => {
        if (!nodeRef.current) return;
        onLeave?.(nodeRef.current);
      }}
      onExited={() => {
        if (!nodeRef.current) return;
        onAfterLeave?.(nodeRef.current);
      }}
    >
      {childWithRef}
    </CSSTransition>
  );

  if (mode) {
    return <SwitchTransition mode={mode}>{transitionNode}</SwitchTransition>;
  }

  return <TransitionGroup component={null}>{transitionNode}</TransitionGroup>;
}
